import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../../../core/auth";
import { env } from "../../../../../env";
import useApi from "../../../../../shared/hooks/useApi";
import { phoneFormatter } from "../../../../../shared/utils/phone-formatter";
import { useIndexedDraftList } from "../../../../entityProfileModal/hooks/useIndexedDraftList";
import useEntityProfileToast from "../../../../entityProfileModal/hooks/useEntityProfileToast";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import type { EditPatientContact } from "../patientContacts/patientContacts.utils";
import type {
  EmergencyContact,
  PatchPatientBody,
  PreparednessField,
} from "./emergencyPreparedness.types";
import {
  createEmptyEmergencyContact,
  createPreparednessDraft,
  getEmergencyContactsValidationMessage,
  normalizeEmergencyContact,
} from "./emergencyPreparedness.utils";

export function usePatientProfileEmergencyPreparednessController(patient: PatientProfilePatient) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();
  const authState = auth.getAuthState();
  const canEdit = auth.isAllowedTo("edit_patient_page_emergency");
  const isOnboardingAgency =
    authState.type === "authenticated" && authState.data.agency.id === env.MEDFLYT_AGENCY_ID;
  const emergencyContacts = React.useMemo(
    () => patient.emergencyContact ?? [],
    [patient.emergencyContact]
  );
  const [isEditingContacts, setIsEditingContacts] = React.useState(false);
  const draftEmergencyContacts = useIndexedDraftList<EmergencyContact>();
  const resetDraftEmergencyContacts = draftEmergencyContacts.resetItems;
  const [contactsValidationMessage, setContactsValidationMessage] = React.useState<string | null>(
    null
  );
  const [activePreparednessField, setActivePreparednessField] =
    React.useState<PreparednessField | null>(null);
  const [isEditingPreparedness, setIsEditingPreparedness] = React.useState(false);
  const [preparednessDraft, setPreparednessDraft] = React.useState(() =>
    createPreparednessDraft(patient)
  );

  const mapExistingContactsToEditContacts = () =>
    (patient.contacts ?? [])
      .filter((contact) => contact.isEmergencyContact === false)
      .map(
        (contact): EditPatientContact => ({
          id: contact.id,
          firstName: contact.firstName,
          middleName: contact.middleName,
          lastName: contact.lastName,
          relationship: contact.relationship,
          mobilePhoneNumber: contact.mobilePhoneNumber,
          homePhoneNumber: contact.homePhoneNumber,
          email: contact.email,
          address: contact.address,
          addressComponents: contact.addressComponents,
          address2: contact.address2,
          isEmergencyContact: false,
          livesWithPatient: contact.livesWithPatient,
        })
      );

  const mapEmergencyContactsToEditContacts = (contacts: EmergencyContact[]) =>
    contacts.map(
      (contact): EditPatientContact => ({
        firstName: contact.firstName,
        middleName: null,
        lastName: contact.lastName,
        relationship: contact.relationship,
        mobilePhoneNumber: contact.emergencyPhoneNumber1,
        homePhoneNumber: contact.emergencyPhoneNumber2 || null,
        email: contact.email || null,
        address: contact.address || null,
        addressComponents: null,
        address2: null,
        isEmergencyContact: true,
        livesWithPatient: contact.livesWithPatient,
      })
    );

  React.useEffect(() => {
    if (isEditingContacts || isEditingPreparedness || activePreparednessField !== null) {
      return;
    }

    resetDraftEmergencyContacts(emergencyContacts.map((contact) => ({ ...contact })));
    setPreparednessDraft(createPreparednessDraft(patient));
  }, [
    activePreparednessField,
    emergencyContacts,
    isEditingContacts,
    isEditingPreparedness,
    isOnboardingAgency,
    patient,
    resetDraftEmergencyContacts,
  ]);

  const updatePatientMutation = useMutation({
    mutationFn: (body: PatchPatientBody) =>
      api.patch("/agencies/:agencyId/patients/:patientId", {
        path: { patientId: patient.id },
        body,
      }),
    onSuccess: (_, body) => {
      queryClient.setQueryData(["patient", "get", patient.id], (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        return {
          ...currentPatient,
          ...body,
        };
      });
      toast({
        description: "Patient details updated successfully.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Could not update patient details.",
        status: "error",
      });
    },
  });
  const updateMainPhoneNumberMutation = useMutation({
    mutationFn: (mainPhoneNumber: string | null) =>
      api.patch("/agencies/:agencyId/patients/:patientId", {
        path: { patientId: patient.id },
        body: { id: patient.id, mainPhoneNumber },
      }),
    onSuccess: (_, mainPhoneNumber) => {
      queryClient.setQueryData(["patient", "get", patient.id], (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        return {
          ...currentPatient,
          mainPhoneNumber,
        };
      });
      toast({
        description:
          mainPhoneNumber === null ? "Main phone number removed." : "Main phone number updated.",
        status: "success",
      });
    },
    onError: () => {
      toast({ description: "Could not update main phone number.", status: "error" });
    },
  });
  const saveEmergencyContactsMutation = useMutation({
    mutationFn: async (emergencyContactsToSave: EmergencyContact[]) => {
      await api.patch("/agencies/:agencyId/patients/:patientId", {
        path: { patientId: patient.id },
        body: { id: patient.id, emergencyContact: emergencyContactsToSave },
      });

      if (!isOnboardingAgency) {
        return null;
      }

      return api.post("./patients/:patientId/contacts", {
        path: { patientId: patient.id },
        body: {
          contacts: [
            ...mapEmergencyContactsToEditContacts(emergencyContactsToSave),
            ...mapExistingContactsToEditContacts(),
          ],
        },
      });
    },
    onSuccess: (contactsResponse, emergencyContactsToSave) => {
      queryClient.setQueryData(["patient", "get", patient.id], (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        return {
          ...currentPatient,
          emergencyContact: emergencyContactsToSave,
          ...(contactsResponse === null
            ? {}
            : {
                contacts: contactsResponse.contacts,
                familyInformation: contactsResponse.contacts
                  .filter((contact) => contact.isEmergencyContact === false)
                  .map((contact) => ({
                    firstName: contact.firstName,
                    lastName: contact.lastName,
                    relationship: contact.relationship,
                    mobilePhoneNumber: contact.mobilePhoneNumber,
                    homePhoneNumber: contact.homePhoneNumber,
                  })),
              }),
        };
      });
      setContactsValidationMessage(null);
      setIsEditingContacts(false);
      toast({
        description: "Successfully updated emergency contacts.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Could not update emergency contacts.",
        status: "error",
      });
    },
  });

  const startEditing = () => {
    resetDraftEmergencyContacts(emergencyContacts.map((contact) => ({ ...contact })));
    setContactsValidationMessage(null);
    setIsEditingContacts(true);
  };
  const stopEditing = () => {
    resetDraftEmergencyContacts(emergencyContacts.map((contact) => ({ ...contact })));
    setContactsValidationMessage(null);
    setIsEditingContacts(false);
  };
  const startEditingPreparedness = () => {
    setPreparednessDraft(createPreparednessDraft(patient));
    setActivePreparednessField(null);
    setIsEditingPreparedness(true);
  };
  const stopEditingPreparedness = () => {
    setPreparednessDraft(createPreparednessDraft(patient));
    setActivePreparednessField(null);
    setIsEditingPreparedness(false);
  };
  const cancelPreparednessField = () => {
    setPreparednessDraft(createPreparednessDraft(patient));
    setActivePreparednessField(null);
  };
  const commitPreparednessField = (field: PreparednessField) => {
    updatePatientMutation.mutate(
      {
        id: patient.id,
        [field]: preparednessDraft[field],
      },
      {
        onSuccess: () => setActivePreparednessField(null),
      }
    );
  };
  const saveEmergencyContacts = () => {
    const validationMessage = getEmergencyContactsValidationMessage(draftEmergencyContacts.items);

    if (validationMessage !== null) {
      setContactsValidationMessage(validationMessage);
      return;
    }

    saveEmergencyContactsMutation.mutate(
      draftEmergencyContacts.items.map(normalizeEmergencyContact)
    );
  };
  const updateMainPhoneNumber = (nextMainPhoneNumber: string | null) => {
    const currentMainPhoneNumber =
      (patient as { mainPhoneNumber?: string | null }).mainPhoneNumber ?? null;
    const isRemoving = nextMainPhoneNumber === currentMainPhoneNumber;
    const replacingNumberString =
      currentMainPhoneNumber === null
        ? ""
        : ` from ${phoneFormatter.formatNationalIfValid(currentMainPhoneNumber)}`;
    const message = isRemoving
      ? "This patient's main phone will be deleted."
      : `This patient's main phone number will be changed${replacingNumberString} to ${phoneFormatter.formatNationalIfValid(
          nextMainPhoneNumber ?? ""
        )}.`;

    if (window.confirm(message) === false) {
      return;
    }

    updateMainPhoneNumberMutation.mutate(isRemoving ? null : nextMainPhoneNumber);
  };

  return {
    activePreparednessField,
    addEmergencyContact: () =>
      draftEmergencyContacts.addItem(() => createEmptyEmergencyContact(isOnboardingAgency)),
    canEdit,
    cancelPreparednessField,
    commitPreparednessField,
    contactsValidationMessage,
    draftEmergencyContacts: draftEmergencyContacts.items,
    emergencyContacts,
    isEditingContacts,
    isEditingPreparedness,
    isOnboardingAgency,
    isSaving:
      updatePatientMutation.isPending ||
      saveEmergencyContactsMutation.isPending ||
      updateMainPhoneNumberMutation.isPending,
    preparednessDraft,
    removeEmergencyContact: draftEmergencyContacts.removeItem,
    saveEmergencyContacts,
    setActivePreparednessField,
    setPreparednessDraft,
    startEditing,
    startEditingPreparedness,
    stopEditing,
    stopEditingPreparedness,
    updateEmergencyContact: draftEmergencyContacts.updateItem,
    updateMainPhoneNumber,
  };
}
